/**
 * Lists Statistics Tab
 * Uses shared CampaignStatsDashboard with lists-specific data.
 * Supports Copy as draft from selected lists.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useCountry, smswiserKeys } from '@/hooks';
import type { ListPerformanceTagsResponse } from '@/hooks';
import {
  useLeadsForFetch,
  useFetchLeadsCounts,
} from '@/hooks';
import {
  useCountryCreative,
  useCreatePreparedDropFlex,
  useDeleteCountrySender,
  useDeleteCountryText,
  useListsWithStats,
  useListsCrossStats,
  useUpdateCountryCreative,
} from '@/hooks';
import {
  filterSenderNamesMax11,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { executeCreateFromListsConfirm } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/hooks/scale/handlers/createFromListsHandler';
import ScaleDropModal from '@/features/smswiser-campaigns/components/ScaleDropModal';
import { listsToDropsWithStatsRow } from '@/features/smswiser-campaigns/utils/preparedDropToScaleRow';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { FetchLeadCountConfirmModal } from '@/features/smswiser-campaigns/components/ListsStatsTab/FetchLeadCountConfirmModal';
import { TagListsDialog } from '@/features/smswiser-campaigns/components/ListsStatsTab/tag-lists';
import {
  CampaignStatsDashboard,
  TruncatedListCell,
  FILTER_FIELDS_LISTS,
  GROUP_BY_OPTIONS_LISTS,
  crossPairForGroupBy,
  buildCrossStatsGroups,
  toRangeISO,
  toGenericStatsRowFromList,
  useStatsTabState,
  useStatsCompareState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import type { ListsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { useToast } from '@/providers/ToastProvider';
import { getDefaultCampaign } from '@/features/smswiser-campaigns/utils/defaultCampaignStorage';
import { resolveTagsForListRow } from './utils';

interface ListsStatsTabProps {
  countryKey: string;
  wiserIds: number[];
  workDate: string;
  /** Perf tags from group-level fetch (Stats section). Passed from parent so fetch runs on any tab. */
  perfTagsQuery: UseQueryResult<ListPerformanceTagsResponse>;
}

export const ListsStatsTab: FC<ListsStatsTabProps> = ({
  countryKey,
  wiserIds,
  workDate,
  perfTagsQuery,
}) => {
  const state = useStatsTabState(workDate, 'lists');
  const [showFetchLeadCountModal, setShowFetchLeadCountModal] = useState(false);
  const [showTagListsModal, setShowTagListsModal] = useState(false);
  const [resolvedFetchCount, setResolvedFetchCount] = useState(0);
  const [createFromListsWiserId, setCreateFromListsWiserId] = useState<number>(0);
  const queryClient = useQueryClient();
  const { hasPermission, user } = useAuth();
  const country = useCountry(countryKey, workDate);

  useEffect(() => {
    if (!country.data?.campaigns?.length) return;
    const def = getDefaultCampaign(countryKey);
    const valid = def != null && country.data.campaigns.some((c) => c.id === def);
    if (valid) setCreateFromListsWiserId((prev) => (prev === 0 ? def : prev));
  }, [countryKey, country.data?.campaigns]);
  const creative = useCountryCreative(countryKey);
  const leadsForFetch = useLeadsForFetch(countryKey);
  const fetchLeadsCounts = useFetchLeadsCounts();
  const createPreparedFlex = useCreatePreparedDropFlex();
  const updateCreative = useUpdateCountryCreative(countryKey);
  const deleteCampaignSender = useDeleteCountrySender(countryKey);
  const deleteCampaignText = useDeleteCountryText(countryKey);
  const { success: toastSuccess, error: toastError } = useToast();

  const handleAddSender = useCallback(
    async (name: string) => {
      if (name.length > 11) return;
      const next = uniqueKeepOrderStrings([...(creative.data?.senderNames ?? []), name]).slice(0, 50);
      await updateCreative.mutateAsync({ senderNames: filterSenderNamesMax11(next) });
    },
    [creative.data?.senderNames, updateCreative],
  );

  const handleAddText = useCallback(
    async (texts: string[]) => {
      const merged = uniqueKeepOrderStrings([...(creative.data?.smsTexts ?? []), ...texts]).slice(0, 50);
      await updateCreative.mutateAsync({ smsTexts: merged });
    },
    [creative.data?.smsTexts, updateCreative],
  );

  const handleDeleteSender = useCallback(
    async (name: string) => {
      await deleteCampaignSender.mutateAsync(name);
    },
    [deleteCampaignSender],
  );

  const handleDeleteText = useCallback(
    async (text: string) => {
      await deleteCampaignText.mutateAsync(text);
    },
    [deleteCampaignText],
  );

  const routes = country.data?.routes ?? [];
  const leadsLists = country.data?.leadsLists ?? [];
  const tlds = country.data?.shortLinkTlds ?? [];
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  const cross = crossPairForGroupBy(state.groupBy);
  const isCross = cross != null;

  const tagsByListId = perfTagsQuery.data?.tagsByListId ?? {};
  const tagsByWiserId = perfTagsQuery.data?.tagsByWiserId ?? {};
  const query = useListsWithStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    enabled: !isCross,
  });

  const crossQuery = useListsCrossStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    cross,
  });

  const activeQuery = isCross ? crossQuery : query;

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = useListsWithStats({
    countryKey,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    enabled: compare.compareMode && !isCross,
  });
  const compareById = useMemo(() => {
    const map = new Map<string, Record<string, number> | null>();
    (compareQuery.data?.rows ?? []).forEach((r, i) => {
      const g = toGenericStatsRowFromList(r, i);
      map.set(g.id, g.voluum);
    });
    return map;
  }, [compareQuery.data?.rows]);

  const allListRows = query.data?.rows ?? [];
  const singleGenericRows = useMemo(() => {
    return allListRows.map((r, i) => {
      const base = toGenericStatsRowFromList(r, i);
      const tags = resolveTagsForListRow(
        r,
        tagsByListId,
        tagsByWiserId,
        leadsLists,
      );
      return { ...base, performanceTags: tags };
    });
     
  }, [allListRows, tagsByListId, tagsByWiserId, leadsLists]);

  /** All tags from API response — New, Unused, and every defined rule tag name (from tagColors). */
  const availableTags = useMemo(() => {
    const set = new Set<string>(['New', 'Unused']);
    const colors = perfTagsQuery.data?.tagColors ?? {};
    for (const name of Object.keys(colors)) if (name.trim()) set.add(name.trim());
    const byListId = perfTagsQuery.data?.tagsByListId ?? {};
    const byWiserId = perfTagsQuery.data?.tagsByWiserId ?? {};
    for (const tags of Object.values(byListId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    for (const tags of Object.values(byWiserId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [perfTagsQuery.data?.tagColors, perfTagsQuery.data?.tagsByListId, perfTagsQuery.data?.tagsByWiserId]);

  const single = useFilteredAndSortedRowsGeneric(
    singleGenericRows,
    state.filtersState,
    state.sort,
    workDate,
    state.groupBy,
  );

  const crossGroups = useMemo(
    () => buildCrossStatsGroups(crossQuery.data, state.sort),
    [crossQuery.data, state.sort],
  );
  const crossFlatRows = useMemo(
    () => crossGroups.flatMap((g) => g.rows),
    [crossGroups],
  );

  const rows = isCross ? crossFlatRows : single.rows;
  const groups = isCross ? crossGroups : single.groups;
  const totals = activeQuery.data?.totals ?? {};
  const allRowsLength = isCross ? crossFlatRows.length : singleGenericRows.length;

  const selectedListsForModal = useMemo(
    () =>
      state.selectedRows
        .map((r) => r._originalList)
        .filter((l): l is ListsWithStatsRow => !!l),
    [state.selectedRows],
  );

  const handleOpenFetchLeadCountModal = useCallback(() => {
    const catalogLeads = leadsForFetch.data?.leads ?? [];
    const leadIds = selectedListsForModal
      .map((row) => {
        const lead = catalogLeads.find(
          (l) =>
            String(l.wiserId) === String(row.listId) ||
            (row.listName &&
              (l.name ?? '').trim().toLowerCase() === row.listName.trim().toLowerCase()),
        );
        return lead?.id;
      })
      .filter((id): id is string => !!id);
    if (leadIds.length === 0) {
      toastError('Fetch lead count', 'No matching lists found in catalog for selected rows.');
      return;
    }
    setResolvedFetchCount(leadIds.length);
    setShowFetchLeadCountModal(true);
  }, [leadsForFetch.data?.leads, selectedListsForModal, toastError]);

  const handleFetchLeadCount = useCallback(async () => {
    const catalogLeads = leadsForFetch.data?.leads ?? [];
    const leadIds = selectedListsForModal
      .map((row) => {
        const lead = catalogLeads.find(
          (l) =>
            String(l.wiserId) === String(row.listId) ||
            (row.listName &&
              (l.name ?? '').trim().toLowerCase() === row.listName.trim().toLowerCase()),
        );
        return lead?.id;
      })
      .filter((id): id is string => !!id);
    if (leadIds.length === 0) {
      toastError('Fetch lead count', 'No matching lists found in catalog for selected rows.');
      return;
    }
    try {
      await fetchLeadsCounts.mutateAsync({
        countryKeys: [countryKey],
        leadIds,
      });
      await queryClient.invalidateQueries({ queryKey: smswiserKeys.country(countryKey) });
      setShowFetchLeadCountModal(false);
      toastSuccess('Lead count fetched', `Fetched counts for ${leadIds.length} list(s).`);
    } catch (e) {
      toastError('Fetch lead count failed', getApiErrorMessage(e, 'Could not fetch lead count'));
    }
  }, [
    leadsForFetch.data?.leads,
    selectedListsForModal,
    countryKey,
    fetchLeadsCounts,
    queryClient,
    toastSuccess,
    toastError,
  ]);

  const campaigns = useMemo(
    () =>
      (country.data?.campaigns ?? []).filter((c) => wiserIds.includes(c.id)),
    [country.data?.campaigns, wiserIds],
  );
  const campaignsForModal =
    campaigns.length > 0
      ? campaigns.map((c) => ({ id: c.id, name: c.name }))
      : (country.data?.campaigns ?? []).map((c) => ({ id: c.id, name: c.name }));

  const handleCreateFromLists = useCallback(
    async (payload: {
      workDate: string;
      routeId: number;
      leadsListWiserId: number;
      shortLinkTldValue: string;
      amount: number;
      executeAfter?: string;
      senderNames: string[];
      smsTexts: string[];
      forceSend?: boolean;
      batchAmount?: number;
      batchDelay?: number;
      useOnlyNewDomains?: boolean;
      clickIdStructure?: ShortlinkClickIdStructure;
      domainScheme?: ShortlinkDomainScheme;
      staticTagId?: string | null;
      scheduleIntervalMinutes?: number;
      routeIdOverride?: number | null;
      shortLinkTldOverride?: string | null;
      senderNamesOverride?: string[] | null;
      smsTextsOverride?: string[] | null;
      smsTextsOverridePerDrop?: string[][];
    }, rows: import('@/features/smswiser-campaigns/types/workspace.types').DropsWithStatsRow[]) => {
      await executeCreateFromListsConfirm(payload, rows, {
        countryKey,
        wiserId: createFromListsWiserId,
        leadsLists,
        routes,
        tlds: tlds.map((t) => ({ value: t.value, displayName: t.displayName })),
        creativeSmsTexts: creative.data?.smsTexts,
        updateCreative,
        createPreparedFlex,
        toastError,
      });
      state.setSelectedRows(() => []);
      state.setShowCopyAsDraftModal(false);
      const dateFormatted = formatDateDMY(payload.workDate);
      toastSuccess('Copied as draft', `${rows.length} draft(s) created for ${dateFormatted}. Check Prepared drops tab.`);
    },
    [
      countryKey,
      createFromListsWiserId,
      leadsLists,
      routes,
      tlds,
      creative.data?.smsTexts,
      updateCreative,
      createPreparedFlex,
      toastError,
      toastSuccess,
      state,
    ],
  );

  const isDateRangeToday =
    state.fromDate === workDate && state.toDate === workDate;

  const actionButtons =
    !isCross && state.selectedRows.length > 0 ? (
      <div className="flex items-center gap-2">
        {hasPermission('smswiser.catalog') && isDateRangeToday && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={
              fetchLeadsCounts.isPending || leadsForFetch.isLoading || country.isLoading
            }
            onClick={handleOpenFetchLeadCountModal}
          >
            Fetch lead count{state.selectedRows.length > 1 ? ` (${state.selectedRows.length})` : ''}
          </Button>
        )}
        {hasPermission('smswiser.list_tag_rules') && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={country.isLoading}
            onClick={() => setShowTagListsModal(true)}
          >
            Tag lists{state.selectedRows.length > 1 ? ` (${state.selectedRows.length})` : ''}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={createPreparedFlex.isPending || country.isLoading}
          onClick={() => {
            setCreateFromListsWiserId(campaignsForModal[0]?.id ?? 0);
            state.setShowCopyAsDraftModal(true);
          }}
        >
          Copy as draft{state.selectedRows.length > 1 ? ` (${state.selectedRows.length})` : ''}
        </Button>
      </div>
    ) : undefined;

  const tagDetailsByListId = perfTagsQuery.data?.tagDetailsByListId ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;

  const tagDetailsLookup = useCallback(
    (listId: string) => {
      if (!listId) return undefined;
      const direct = tagDetailsByListId[listId];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsByListId).find(
        ([k]) => k.toLowerCase() === listId.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsByListId],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      if (isCross) {
        // List ▸ Source tree: group header is the list; leaf rows are sources.
        return (
          <span className="block truncate text-sm text-foreground">
            {row.label || '—'}
          </span>
        );
      }
      const listRow = row._originalList;
      const listId = listRow?.listId ? String(listRow.listId).trim() : '';
      const leadsList = listRow
        ? leadsLists.find(
            (l) =>
              (listRow.listName &&
                (l.name ?? '').trim().toLowerCase() === listRow.listName.trim().toLowerCase()) ||
              (listId && String(l.id) === listId),
          )
        : null;
      const performanceTags = resolveTagsForListRow(
        { listId, listName: listRow?.listName },
        tagsByListId,
        tagsByWiserId,
        leadsLists,
      );
      const performanceTagDetails = listId ? tagDetailsLookup(listId) : undefined;
      return (
        <TruncatedListCell
          label={row.label ?? '—'}
          listId={row.id}
          leadCount={leadsList ? leadsList.count : undefined}
          usageToday={leadsList?.usageToday}
          remaining={leadsList?.remaining}
          isDateRangeToday={isDateRangeToday}
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
          tagsLoading={perfTagsQuery.isFetching}
        />
      );
    },
    [isCross, leadsLists, isDateRangeToday, tagsByListId, tagsByWiserId, tagDetailsLookup, tagColors, perfTagsQuery.isFetching],
  );

  return (
    <>
      <CampaignStatsDashboard
        tab="lists"
        title="List"
        emptyMessage="No lists found for this range."
        loadingTitle="Loading list performance data"
        loadingDesc="Fetching tracker metrics grouped by leads list."
        errorPrefix="Failed to load list stats."
        firstColumnLabel="List"
        firstColumnSortKey="list"
        rowUnitLabel="list"
        filterFields={FILTER_FIELDS_LISTS}
        groupByOptions={GROUP_BY_OPTIONS_LISTS}
        rows={rows}
        totals={totals}
        groups={groups}
        totalRowCount={activeQuery.data?.total ?? allRowsLength}
        allRowsLength={allRowsLength}
        currency={activeQuery.data?.currency ?? 'USD'}
        fromDate={state.fromDate}
        toDate={state.toDate}
        onFromChange={state.setFromDate}
        onToChange={state.setToDate}
        onDateRangeChange={state.setDateRange}
        length={state.length}
        onLengthChange={state.setLength}
        filtersState={state.filtersState}
        setFilters={state.setFilters}
        defaultFilter={state.defaultFilter}
        groupBy={state.groupBy}
        setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
        collapsedGroups={state.collapsedGroups}
        setCollapsedGroups={state.setCollapsedGroups}
        toggleGroup={state.toggleGroup}
        sort={state.sort}
        onSortClick={state.handleSortClick}
        selectedRows={state.selectedRows}
        setSelectedRows={state.setSelectedRows}
        renderFirstCell={renderFirstCell}
        isError={activeQuery.isError ?? false}
        error={activeQuery.error}
        isLoading={activeQuery.isLoading ?? false}
        isFetching={activeQuery.isFetching ?? false}
        dataUpdatedAt={activeQuery.dataUpdatedAt}
        refetch={() => activeQuery.refetch()}
        actionButtons={actionButtons}
        showLimitSelect={false}
        showGroupBySelect
        csvExportUserId={user?.id}
        tagFilterOptions={availableTags}
        tagFilterColors={tagColors}
        tagsLoading={perfTagsQuery.isLoading}
        compareCapable={!isCross}
        compareMode={compare.compareMode}
        onToggleCompare={compare.toggleCompareMode}
        compareFromDate={compare.compareFrom}
        compareToDate={compare.compareTo}
        onCompareFromChange={compare.setCompareFrom}
        onCompareToChange={compare.setCompareTo}
        onCompareRangeChange={compare.setCompareRange}
        compareById={compareById}
        compareTotals={compareQuery.data?.totals ?? {}}
        onComparePreset={compare.applyPreset}
        granularity={compare.granularity}
        onGranularityChange={compare.setGranularity}
        countryKey={countryKey}
        wiserIds={wiserIds}
      />

      {showTagListsModal && (
        <TagListsDialog
          countryKey={countryKey}
          rows={selectedListsForModal}
          leadsLists={leadsLists}
          onClose={() => setShowTagListsModal(false)}
          onDone={() => {
            setShowTagListsModal(false);
            state.setSelectedRows(() => []);
          }}
        />
      )}

      {showFetchLeadCountModal && (
        <FetchLeadCountConfirmModal
          listCount={resolvedFetchCount}
          onClose={() => setShowFetchLeadCountModal(false)}
          onConfirm={handleFetchLeadCount}
          isBusy={fetchLeadsCounts.isPending}
        />
      )}

      {state.showCopyAsDraftModal && selectedListsForModal.length > 0 && (
        <ScaleDropModal
          countryKey={countryKey}
          mode="create-from-lists"
          rows={listsToDropsWithStatsRow(selectedListsForModal, countryKey)}
          gateways={routes}
          campaigns={campaignsForModal}
          leadsLists={leadsLists}
          tlds={tlds.map((t) => ({ value: t.value, displayName: t.displayName }))}
          senderNames={creative.data?.senderNames ?? []}
          smsTexts={creative.data?.smsTexts ?? []}
          smsTextsWithIds={creative.data?.smsTextsWithIds}
          defaultWorkDate={workDate}
          titlePrefix={creative.data?.titlePrefix ?? ''}
          isBusy={
            createPreparedFlex.isPending ||
            updateCreative.isPending ||
            deleteCampaignSender.isPending ||
            deleteCampaignText.isPending
          }
          onClose={() => state.setShowCopyAsDraftModal(false)}
          createFromListsWiserId={createFromListsWiserId}
          onCreateFromListsWiserIdChange={setCreateFromListsWiserId}
          onCreateFromLists={handleCreateFromLists}
          onAddSender={handleAddSender}
          onAddText={handleAddText}
          onDeleteSender={handleDeleteSender}
          onDeleteText={handleDeleteText}
          perfTagsQuery={perfTagsQuery}
        />
      )}
    </>
  );
};

export default ListsStatsTab;
